/**
 * Sugar Town Commerce — Stage 1 Commerce Adapter (read-only facade).
 *
 * WHAT THIS IS
 * ------------
 * A single, framework-agnostic seam that the storefront reads its catalog
 * data through. Today every method delegates to the existing Supabase-backed
 * `commerce/api.ts` layer, so this change introduces NO runtime behaviour
 * change — it is a pure indirection.
 *
 * WHY IT EXISTS
 * -------------
 * The long-term architecture is:
 *
 *     Customer → Sugar Town Website → Tallivo Commerce API → data
 *
 * The Tallivo Commerce API is not built yet. Rather than rewrite the
 * storefront in a big-bang migration later, we introduce this adapter now as
 * the boundary where the data source can be swapped, one domain at a time,
 * without touching UI components or stores.
 *
 * HOW TO MIGRATE TO TALLIVO (later, per-domain)
 * ---------------------------------------------
 * 1. Implement a `TallivoCommerceSource` that satisfies `CommerceSource`.
 * 2. Flip individual methods (e.g. `getProducts`) to the Tallivo
 *    implementation behind a feature flag / env switch in `resolveSource()`.
 * 3. Leave the rest pointing at Supabase until each domain's Tallivo
 *    endpoint is ready. The UI never changes.
 *
 * RULES
 * -----
 * - Read-only. Catalog/pricing/inventory/collections/shipping reads only.
 * - Cart mutations still live in the cart store / api.ts (write path is a
 *   separate Stage). This facade is the read seam.
 * - UI + stores should import from THIS module for catalog reads going
 *   forward, not directly from `api.ts`.
 */
import * as api from "./api";
import * as search from "./search";
import * as discovery from "./discovery";
import type {
  CommerceBrand,
  CommerceCategory,
  CommerceCollection,
  CommerceProduct,
  CommerceProductFull,
  CommerceCartLine,
} from "./types";
import type { ProductFilter } from "./api";
import type { SearchableProduct } from "./search";

/** Identifies where a given read was served from — useful for debugging. */
export type CommerceSourceId = "supabase" | "tallivo";

/**
 * The read contract every commerce data source must satisfy. When the Tallivo
 * Commerce API exists, a `TallivoCommerceSource` implements this same shape,
 * and `resolveSource()` picks which one serves each call.
 */
export interface CommerceSource {
  readonly id: CommerceSourceId;
  getBrands(): Promise<CommerceBrand[]>;
  getCategories(): Promise<CommerceCategory[]>;
  getCollections(featuredOnly?: boolean): Promise<CommerceCollection[]>;
  getProducts(filter?: ProductFilter): Promise<CommerceProduct[]>;
  getProductBySlug(slug: string): Promise<CommerceProductFull | null>;
  getProductsByIds(ids: string[]): Promise<CommerceProduct[]>;
  getCartRecommendations(productIdsInCart: string[], limit?: number): Promise<CommerceProduct[]>;
}

/**
 * The current source: Supabase native commerce tables via `api.ts`.
 * Delegates verbatim — behaviour is identical to calling `api.*` directly.
 */
const supabaseSource: CommerceSource = {
  id: "supabase",
  getBrands: () => api.getBrands(),
  getCategories: () => api.getCategories(),
  getCollections: (featuredOnly) => api.getCollections(featuredOnly),
  getProducts: (filter) => api.getProducts(filter),
  getProductBySlug: (slug) => api.getProductBySlug(slug),
  getProductsByIds: (ids) => api.getProductsByIds(ids),
  getCartRecommendations: (ids, limit) => api.getCartRecommendations(ids, limit),
};

/**
 * Choose the active source. Today this always returns Supabase. When Tallivo
 * is ready, switch specific domains here (e.g. read an env flag) without
 * changing any caller.
 */
function resolveSource(): CommerceSource {
  return supabaseSource;
}

/** The active commerce source. Import this for catalog reads. */
export const commerce: CommerceSource = resolveSource();

// Convenience re-exports so callers can migrate imports incrementally
// (`import { getProducts } from "@/lib/commerce/adapter"`).
export const getBrands = () => commerce.getBrands();
export const getCategories = () => commerce.getCategories();
export const getCollections = (featuredOnly?: boolean) => commerce.getCollections(featuredOnly);
export const getProducts = (filter?: ProductFilter) => commerce.getProducts(filter);
export const getProductBySlug = (slug: string) => commerce.getProductBySlug(slug);
export const getProductsByIds = (ids: string[]) => commerce.getProductsByIds(ids);
export const getCartRecommendations = (ids: string[], limit?: number) =>
  commerce.getCartRecommendations(ids, limit);

// ---------- Extended read seams (Stage 2) ----------
//
// These domains live in dedicated modules (search snapshot, merchandising /
// featured shelves, cart-line hydration) but are surfaced HERE so the whole
// storefront reads its catalog data through a single boundary. Each delegates
// verbatim today — zero runtime behaviour change — and can later be pointed at
// Tallivo without touching any caller.

/** Search / faceted-filter catalog snapshot (product + variant + price + brand). */
export const getCatalogSnapshot = (force?: boolean): Promise<SearchableProduct[]> =>
  search.getCatalogSnapshot(force);
export const invalidateCatalogSnapshot = () => search.invalidateCatalogSnapshot();

/** Featured-product shelf for the homepage / shop. */
export const getFeaturedProducts = (limit?: number): Promise<SearchableProduct[]> =>
  discovery.getFeaturedProducts(limit);

/** Cart-line hydration read (mutations remain in the cart store / api.ts). */
export const getCartLines = (cartId: string): Promise<CommerceCartLine[]> =>
  api.getCartLines(cartId);

// ---------- Inventory availability (Stage 3) ----------
//
// Inventory reads go through a dedicated provider seam so authority can move
// to Tallivo per-domain without touching the catalog adapter. Reads only —
// no reservations, no deductions, no checkout authority change.
import {
  commerceInventory,
  getCartAvailability as _getCartAvailability,
  describeVariantAvailability as _describeVariantAvailability,
  describeCartLineAvailability as _describeCartLineAvailability,
  shouldBlockAddToCart as _shouldBlockAddToCart,
} from "./inventory";
import type {
  CommerceInventoryAvailability,
  CartLineForAvailability,
  CartLineAvailability,
} from "./inventory";

export type {
  CommerceInventoryAvailability,
  CartLineForAvailability,
  CartLineAvailability,
  CommerceInventoryStatus,
} from "./inventory";

export const getVariantAvailability = (variantId: string) =>
  commerceInventory.getVariantAvailability(variantId);
export const getProductAvailability = (productId: string) =>
  commerceInventory.getProductAvailability(productId);
export const getVariantAvailabilityMap = (variantIds: string[]) =>
  commerceInventory.getVariantAvailabilityMap(variantIds);
/** Stage 5 — batched product-level availability (aggregated across variants). */
export const getProductsAvailabilityMap = (productIds: string[]) =>
  commerceInventory.getProductsAvailabilityMap(productIds);
export const getCartAvailability = (lines: CartLineForAvailability[]) =>
  _getCartAvailability(lines);
export const describeVariantAvailability = _describeVariantAvailability;
export const describeCartLineAvailability = _describeCartLineAvailability;
export const shouldBlockAddToCart = _shouldBlockAddToCart;
export { productCardBadge, aggregateVariantStatuses } from "./inventory";

